/**
 * Generate a deterministic equalizer pattern for a track based on its characteristics
 * Uses track ID as a seed to ensure the same track always has the same pattern
 */

/**
 * Simple hash function to generate a number from a string
 */
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Seeded random number generator for reproducible patterns
 */
const seededRandom = (seed, index) => {
  const x = Math.sin(seed * 12.9898 + index * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

/**
 * Generate an equalizer pattern for a track
 * 12 bars with heights 15-85% for dynamic effect
 *
 * @param {Object} track - Spotify track object with name, artists, id
 * @returns {Array<number>} Array of 12 bar heights (15-85%)
 */
export const generateEqualizerPattern = (track) => {
  if (!track || !track.spotify_track_id) {
    // Default pattern if no track
    return [45, 55, 65, 75, 85, 75, 65, 55, 45, 50, 60, 70];
  }

  // Use track ID as seed for reproducible randomness
  const seed = hashString(track.spotify_track_id);
  const pattern = [];
  const numBars = 12;

  // Generate a pattern that has a "peak" in the middle like a real equalizer
  for (let i = 0; i < numBars; i++) {
    // Base height from position (creates peak in middle)
    const positionBias = Math.sin((i / numBars) * Math.PI) * 40; // 0-40 boost at center
    
    // Randomness for variety
    const randomness = seededRandom(seed, i) * 30; // 0-30 variation
    
    // Combine: base position bias + randomness
    let height = 35 + positionBias + randomness;
    
    // Clamp to 15-85% range
    height = Math.max(15, Math.min(85, height));
    
    pattern.push(Math.round(height));
  }

  return pattern;
};

/**
 * Pattern library for specific genre-like characteristics
 * Could be extended with API data in the future
 */
export const PATTERN_TYPES = {
  AMBIENT: [20, 25, 30, 35, 40, 40, 35, 30, 25, 20, 22, 28],
  ELECTRONIC: [50, 45, 60, 70, 80, 85, 75, 60, 50, 55, 65, 72],
  HIP_HOP: [75, 70, 65, 70, 60, 50, 60, 70, 75, 80, 72, 68],
  ROCK: [65, 70, 75, 80, 70, 60, 75, 80, 85, 75, 70, 65],
  POP: [45, 50, 60, 70, 75, 80, 75, 65, 50, 45, 55, 65],
  JAZZ: [40, 45, 50, 55, 60, 55, 50, 45, 40, 50, 60, 55],
};

/**
 * Get pattern based on track characteristics
 * Future enhancement: could use Spotify API audio features
 * (energy, danceability, tempo, etc.) to select pattern
 *
 * @param {Object} track - Spotify track object
 * @returns {Array<number>} Equalizer pattern
 */
export const getTrackPattern = (track) => {
  // Currently generates deterministic pattern from track ID
  // Future: could parse genre or use Spotify audio features API
  return generateEqualizerPattern(track);
};
